import { useEffect, useRef, type ReactNode } from "react";
import { useUIStore } from "@/stores/useUIStore";

const STORAGE_KEY = "wexare-theme";
const TRANSITION_CLASS = "theme-transition";

function applyTheme(theme: "light" | "dark", animate: boolean) {
  const root = document.documentElement;

  if (animate) {
    root.classList.add(TRANSITION_CLASS);
    setTimeout(() => root.classList.remove(TRANSITION_CLASS), 300);
  }

  if (theme === "dark") {
    root.classList.add("dark");
    root.style.colorScheme = "dark";
  } else {
    root.classList.remove("dark");
    root.style.colorScheme = "light";
  }
}

function ThemeProvider({ children }: { children: ReactNode }) {
  const theme = useUIStore((s) => s.theme);
  const setTheme = useUIStore((s) => s.setTheme);
  // Tracks whether the initial DOM application has happened.
  // Starts false so the sync effect skips the first render
  // (the init effect handles that render).
  const ready = useRef(false);

  // Apply the already-correct store value to the DOM on mount (no animation)
  useEffect(() => {
    applyTheme(theme, false);
    ready.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount only, without animation: later changes go through the sync effect below
  }, []);

  // Sync subsequent theme changes → DOM + localStorage
  useEffect(() => {
    if (!ready.current) return;
    applyTheme(theme, true);
    localStorage.setItem(STORAGE_KEY, theme);
  }, [theme]);

  // Listen for system preference changes (only when no stored preference)
  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    function handleChange(e: MediaQueryListEvent) {
      if (!localStorage.getItem(STORAGE_KEY)) {
        setTheme(e.matches ? "dark" : "light");
      }
    }
    mq.addEventListener("change", handleChange);
    return () => mq.removeEventListener("change", handleChange);
  }, [setTheme]);

  // Cross-tab sync via storage event
  useEffect(() => {
    function handleStorage(e: StorageEvent) {
      if (e.key === STORAGE_KEY && (e.newValue === "dark" || e.newValue === "light")) {
        setTheme(e.newValue);
      }
    }
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [setTheme]);

  return <>{children}</>;
}

export { ThemeProvider };
